/**
 * CUSTOM MIGRATION: Free Gift system
 * Migrated from previous theme version (Vision 7.0.0 -> 16.0.0)
 * ---------------------------------------------------------------------------
 * Adds a gift product to the cart when a main product is added, based on
 * these product metafields (namespace "custom"):
 *
 *   custom.gift_product   -> Product reference
 *   custom.gift_quantity  -> Integer
 *
 * The gift line item is tagged with these properties:
 *
 *   _is_gift               : "true"
 *   _gift_for_variant_id   : <main variant id>
 *   _gift_for_product_id   : <main product id>
 *   _gift_ratio            : <gift units per ONE unit of the main product>
 *
 * QUANTITY SCALING
 * ---------------------------------------------------------------------------
 * The gift quantity tracks the parent quantity: buy 2 of a parent whose ratio
 * is 3 and the cart holds 6 gift units. custom.gift_quantity is read as the
 * per-unit ratio.
 *
 * The ratio is persisted on the gift line itself as the hidden `_gift_ratio`
 * property rather than re-read from the DOM, because the data-gift-* attributes
 * only exist on the parent product's own page — a customer changing quantity on
 * /cart has no product-form to read from. Properties prefixed with "_" are not
 * shown to the customer at checkout.
 *
 * IMPORTANT: This file ONLY manages the gift line's presence and quantity. It
 * does NOT make the gift free. A Shopify automatic "Buy X, Get Y" discount (or
 * a Shopify Function) is required to zero out the gift line.
 *
 * !! The discount MUST have "Set a maximum number of uses per order" UNCHECKED.
 *    If it is capped, only the first batch is discounted and the customer is
 *    charged for every additional gift unit this script adds.
 *    The discount's "Customer buys" quantity must equal 1 and its "Customer
 *    gets" quantity must equal custom.gift_quantity, or the two will disagree.
 *
 * v16 COMPATIBILITY CHANGE (see findTitleLink below):
 * The Vision 16 cart drawer wraps the product image in its own
 * `.cart-product-link` anchor, so the old `row.querySelector('.cart-product-link')`
 * would have attached the gift pill next to the IMAGE instead of the TITLE.
 * The lookup now skips any link inside `.product-cart-item-image`.
 *
 * Verified against Vision 16 architecture:
 *   - events `cart:item-added`, `line-item:change:end`, `cart:refresh` still fire
 *   - `cart:item-added` detail.product still carries product_id / variant_id
 *   - sections main-cart / cart-drawer / cart-bubble all still exist
 *   - row ids CartItem-N / CartDrawerItem-N unchanged
 *   - `.remove` class survives the <a> -> <cart-remove> element change
 *
 * CUSTOM FIX (17 Sep 2026) — ONE GIFT LINE PER GIFT VARIANT
 * ---------------------------------------------------------------------------
 * Gift lines used to be tagged per parent (_gift_for_product_id/_gift_ratio).
 * Shopify splits lines into free and full-price parts whenever the discount
 * allowance changes, and it does not spread a Buy X Get Y discount across
 * differently-tagged lines of the same canned food. Result: 10 kg + 4 kg of
 * the same formula (both give Tavuklu Ezme) or lowering 2 bags to 1 left
 * customers paying for "gift" cans.
 * Now every gift line of a variant carries the same properties:
 *   _is_gift   : "true"
 *   _gift_rules: "<parent product id>:<units per parent>,..." (sorted)
 * and the script keeps the TOTAL of that variant equal to
 * sum(parent quantity x units). Legacy per-parent lines are merged on sight.
 *
 * Parents added from anywhere (cart drawer "Ayrıca şunları da beğenebilirsiniz",
 * recommendation cards, quick add) get their gift too: when a cart line's gift
 * config is not on the page, it is read from
 * /products/<handle>?view=gift-config (templates/product.gift-config.liquid)
 * and cached for 10 minutes in sessionStorage.
 *
 * Reversible: delete this file and remove the <script> tag in
 * layout/theme.liquid wrapped by the "custom-free-gift" comment markers.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var ROUTES = (window.theme && window.theme.routes) || {
    cart_add_url: '/cart/add',
    cart_change_url: '/cart/change',
    cart_url: '/cart'
  };

  var SECTIONS = [
    { id: 'Cart',               section: 'main-cart',   selector: '.thb-cart-form'  },
    { id: 'Cart-Drawer',        section: 'cart-drawer', selector: '.cart-drawer'    },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ];

  var SCRIPT_TAG = document.currentScript
    || document.querySelector('script[src*="custom-free-gift.js"]');
  var GIFT_LABEL = (SCRIPT_TAG && SCRIPT_TAG.dataset.giftLabel) || 'Ücretsiz Hediye';

  var inFlight = false;

  // Gift variants whose total Shopify refused to reach (almost always gift
  // stock running out): variant id -> the total we tried. Stops retry loops.
  var cappedVariants = Object.create(null);

  // Rules learned from product pages (cart:item-added) and not yet written to
  // the cart: variant id -> { parent product id: units per parent }.
  var pendingRules = Object.create(null);

  // Gift config per parent product id: { variantId, quantity } or null (no gift).
  var configCache = Object.create(null);
  var CONFIG_TTL = 10 * 60 * 1000;

  function cachedConfig(pid) {
    if (pid in configCache) return configCache[pid];
    try {
      var raw = window.sessionStorage.getItem('afg-cfg:' + pid);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && Date.now() - parsed.t < CONFIG_TTL) {
          configCache[pid] = parsed.c;
          return parsed.c;
        }
      }
    } catch (e) { /* storage unavailable */ }
    return undefined;
  }

  function storeConfig(pid, config) {
    configCache[pid] = config;
    try {
      window.sessionStorage.setItem('afg-cfg:' + pid, JSON.stringify({ c: config, t: Date.now() }));
    } catch (e) { /* storage unavailable */ }
  }

  function fetchGiftConfig(handle, pid) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=gift-config', {
      credentials: 'same-origin'
    }).then(function (r) { return r.ok ? r.text() : ''; }).then(function (text) {
      var a = text.indexOf('{'), b = text.lastIndexOf('}');
      var data = a >= 0 && b > a ? JSON.parse(text.slice(a, b + 1)) : null;
      var config = data && data.variantId && data.quantity > 0
        ? { variantId: String(data.variantId), quantity: parseInt(data.quantity, 10) }
        : null;
      storeConfig(pid, config);
    }).catch(function () { /* unknown for now; retried on the next page */ configCache[pid] = null; });
  }

  // Makes sure every parent line in the cart has a known gift config, then
  // queues its rule. Rules already written to the cart are dropped by
  // prunePending, so this is cheap after the first pass.
  function ensureParentConfigs(cart) {
    var waits = [];
    var seen = Object.create(null);
    (cart.items || []).forEach(function (item) {
      var props = item.properties || {};
      if (props._is_gift === 'true') return;
      var pid = String(item.product_id);
      if (seen[pid]) return;
      seen[pid] = true;
      if (cachedConfig(pid) === undefined && item.handle) waits.push(fetchGiftConfig(item.handle, pid));
    });
    return Promise.all(waits).then(function () {
      for (var pid in seen) {
        var cfg = configCache[pid];
        if (!cfg) continue;
        if (!pendingRules[cfg.variantId]) pendingRules[cfg.variantId] = {};
        pendingRules[cfg.variantId][pid] = cfg.quantity;
      }
    });
  }

  // --- Helpers --------------------------------------------------------------

  function sectionsParam() {
    return SECTIONS.map(function (s) { return s.section; }).join(',');
  }

  function readGiftConfigForProduct(productId) {
    if (!productId) return null;
    var form = document.querySelector(
      'product-form[data-gift-source-product-id="' + productId + '"][data-gift-variant-id]'
    );
    if (!form) return null;
    var variantId = parseInt(form.dataset.giftVariantId, 10);
    var quantity  = parseInt(form.dataset.giftQuantity, 10);
    if (!variantId || !quantity || quantity < 1) return null;
    return {
      variantId: variantId,
      productId: parseInt(form.dataset.giftProductId, 10) || null,
      quantity:  quantity
    };
  }

  function fetchCart() {
    return fetch(ROUTES.cart_url + '.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  // Total quantity of a product across NON-GIFT lines. Gift lines never count
  // as parents — a gift cannot satisfy another gift's parent requirement.
  function parentQuantityFor(cart, productId) {
    if (!cart || !cart.items) return 0;
    var target = String(productId);
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var props = item.properties || {};
      if (props._is_gift === 'true') continue;
      if (String(item.product_id) === target) total += item.quantity;
    }
    return total;
  }

  // Reads both the current (_gift_rules) and the legacy per-parent format.
  function parseRules(props) {
    var rules = {};
    if (!props) return rules;
    if (props._gift_rules) {
      String(props._gift_rules).split(',').forEach(function (pair) {
        var bits = pair.split(':');
        var pid = (bits[0] || '').trim();
        var ratio = parseInt(bits[1], 10);
        if (pid && ratio > 0) rules[pid] = ratio;
      });
    } else if (props._gift_for_product_id) {
      var legacyRatio = parseInt(props._gift_ratio, 10);
      if (legacyRatio > 0) rules[String(props._gift_for_product_id)] = legacyRatio;
    }
    return rules;
  }

  function rulesString(rules) {
    return Object.keys(rules).sort().map(function (pid) {
      return pid + ':' + rules[pid];
    }).join(',');
  }

  // Identity of a line's properties as Shopify sees them (lines with different
  // properties never merge).
  function signature(props) {
    if (props._gift_rules !== undefined) return 'R|' + props._gift_rules;
    return 'L|' + props._gift_for_product_id + '|' + props._gift_ratio + '|' + props._gift_for_variant_id;
  }

  function unitPrice(line) {
    return line.quantity ? line.final_line_price / line.quantity : 0;
  }

  // Builds the cart operations, in order, that make every gift variant's
  // total equal sum(parent quantity x units) with ONE property signature.
  // Only the first operation is applied per pass: Shopify re-keys and re-splits
  // lines after every change, so each pass re-reads the cart.
  //   { type: 'add',    variantId, quantity, rules, desired }
  //   { type: 'change', key, quantity, variantId, desired }
  function computePlan(cart, extras) {
    var plan = [];
    if (!cart || !cart.items) return plan;
    var buckets = Object.create(null);
    var order = [];

    function bucket(v) {
      if (!buckets[v]) {
        buckets[v] = { lines: [], rules: {} };
        order.push(v);
      }
      return buckets[v];
    }

    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      var props = item.properties || {};
      if (props._is_gift !== 'true') continue;
      var b = bucket(String(item.variant_id));
      b.lines.push(item);
      var lineRules = parseRules(props);
      for (var p in lineRules) b.rules[p] = lineRules[p];
    }

    // CUSTOM FIX (17 Sep 2026): each trial pack (product type "Deneme Paketi")
    // can be bought once per order. Bring any higher quantity back to 1 first.
    for (var tq = 0; tq < cart.items.length; tq++) {
      var tItem = cart.items[tq];
      if (tItem.product_type === 'Deneme Paketi' && tItem.quantity > 1 &&
          (tItem.properties || {})._is_gift !== 'true') {
        return [{ type: 'change', key: tItem.key, quantity: 1, variantId: String(tItem.variant_id), desired: 1 }];
      }
    }

    for (var ev in (extras || {})) {
      var eb = bucket(ev);
      for (var ep in extras[ev]) eb.rules[ep] = extras[ev][ep];
    }

    order.forEach(function (v) {
      var bk = buckets[v];
      var pids = Object.keys(bk.rules);
      if (!pids.length) return; // lines we do not understand: leave alone

      var desired = 0;
      pids.forEach(function (pid) { desired += parentQuantityFor(cart, pid) * bk.rules[pid]; });
      var rules = rulesString(bk.rules);
      var target = 'R|' + rules;

      if (desired === 0) {
        if (bk.lines.length) plan.push({ type: 'change', key: bk.lines[0].key, quantity: 0, variantId: v, desired: 0 });
        return;
      }

      var tLines = [], others = [], tSum = 0;
      bk.lines.forEach(function (l) {
        if (signature(l.properties || {}) === target) { tLines.push(l); tSum += l.quantity; }
        else others.push(l);
      });

      if (others.length || !tLines.length) {
        if (tSum < desired) {
          if (cappedVariants[v] === desired) return;
          plan.push({ type: 'add', variantId: v, quantity: desired - tSum, rules: rules, desired: desired });
          return;
        }
        if (others.length) {
          plan.push({ type: 'change', key: others[0].key, quantity: 0, variantId: v, desired: desired });
          return;
        }
      }

      if (tSum === desired) return;

      if (tSum > desired) {
        var paidFirst = tLines.slice().sort(function (x, y) { return unitPrice(y) - unitPrice(x); });
        var cut = Math.min(paidFirst[0].quantity, tSum - desired);
        plan.push({ type: 'change', key: paidFirst[0].key, quantity: paidFirst[0].quantity - cut, variantId: v, desired: desired });
      } else {
        if (cappedVariants[v] === desired) return;
        var freeFirst = tLines.slice().sort(function (x, y) { return unitPrice(x) - unitPrice(y); });
        plan.push({ type: 'change', key: freeFirst[0].key, quantity: freeFirst[0].quantity + (desired - tSum), variantId: v, desired: desired });
      }
    });
    return plan;
  }

  // Forget product-page rules once the cart carries them (or the parent left).
  function prunePending(cart) {
    for (var v in pendingRules) {
      var have = {};
      for (var i = 0; i < (cart.items || []).length; i++) {
        var it = cart.items[i];
        var pr = it.properties || {};
        if (pr._is_gift === 'true' && pr._gift_rules !== undefined && String(it.variant_id) === v) {
          var r = parseRules(pr);
          for (var k in r) have[k] = r[k];
        }
      }
      for (var pid in pendingRules[v]) {
        if (have[pid] === pendingRules[v][pid] || parentQuantityFor(cart, pid) === 0) delete pendingRules[v][pid];
      }
      if (!Object.keys(pendingRules[v]).length) delete pendingRules[v];
    }
  }

  function giftTotal(items, variantId) {
    var total = 0;
    for (var i = 0; i < (items || []).length; i++) {
      var p = items[i].properties || {};
      if (p._is_gift === 'true' && String(items[i].variant_id) === String(variantId)) total += items[i].quantity;
    }
    return total;
  }

  function postJson(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  function postAddGiftLine(variantId, quantity, rules) {
    return postJson(ROUTES.cart_add_url + '.js', {
      items: [{ id: parseInt(variantId, 10), quantity: quantity, properties: { _is_gift: 'true', _gift_rules: rules } }],
      sections: sectionsParam(),
      sections_url: window.location.pathname
    });
  }

  function postCartChangeByKey(lineKey, quantity) {
    return postJson(ROUTES.cart_change_url + '.js', {
      id: lineKey,
      quantity: quantity,
      sections: sectionsParam(),
      sections_url: window.location.pathname
    });
  }

  function renderSections(parsedState) {
    if (!parsedState || !parsedState.sections) return;
    SECTIONS.forEach(function (s) {
      var root = document.getElementById(s.id);
      if (!root) return;
      var target = root.querySelector(s.selector) || root;
      var html = parsedState.sections[s.section];
      if (!html) return;
      try {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var inner = parsed.querySelector(s.selector);
        if (inner) target.innerHTML = inner.innerHTML;
      } catch (e) { /* ignore */ }
    });
    decorateAllGiftLines();
  }

  // --- Add-gift flow --------------------------------------------------------

  function handleItemAdded(evt) {
    var added = evt && evt.detail && evt.detail.product;
    if (!added || !added.product_id) return;
    if (added.properties && added.properties._is_gift === 'true') return;

    var gift = readGiftConfigForProduct(added.product_id);
    // No config on this page (drawer recommendations, quick add...):
    // the sync pass looks it up.
    if (!gift) { scheduleSync(); return; }

    var v = String(gift.variantId);
    storeConfig(String(added.product_id), { variantId: v, quantity: gift.quantity });
    if (!pendingRules[v]) pendingRules[v] = {};
    pendingRules[v][String(added.product_id)] = gift.quantity;
    scheduleSync();
  }

  // --- Gift quantity sync ---------------------------------------------------
  //
  // Always re-fetches /cart.js, builds a plan and applies its first step.
  // Debounced; repeats (bounded) until nothing is left to do.

  var syncTimer = null;
  var syncPasses = 0;

  function scheduleSync() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      syncTimer = null;
      runGiftSync();
    }, 80);
  }

  function applyOp(op, state) {
    if (op.type === 'add') {
      return postAddGiftLine(op.variantId, op.quantity, op.rules).then(function (resp) {
        if (!resp || resp.status || resp.errors) {
          cappedVariants[op.variantId] = op.desired; // e.g. gift out of stock
          return;
        }
        state.last = resp;
      }).catch(function () { cappedVariants[op.variantId] = op.desired; });
    }
    return postCartChangeByKey(op.key, op.quantity).then(function (resp) {
      if (!resp || resp.status || resp.errors) return;
      state.last = resp;
      if (op.quantity > 0 && resp.items && giftTotal(resp.items, op.variantId) < op.desired) {
        cappedVariants[op.variantId] = op.desired;
      }
    }).catch(function () {});
  }

  function runGiftSync() {
    if (inFlight) {
      setTimeout(scheduleSync, 120);
      return;
    }
    inFlight = true;

    fetchCart()
      .then(function (cart) {
        return ensureParentConfigs(cart).then(function () { return cart; });
      })
      .then(function (cart) {
        prunePending(cart);
        var plan = computePlan(cart, pendingRules);
        if (plan.length === 0) return false;
        var state = { last: null };
        return applyOp(plan[0], state).then(function () {
          if (state.last) renderSections(state.last);
          return true;
        });
      })
      .catch(function () { return false; })
      .then(function (progressed) {
        inFlight = false;
        // One operation per pass; keep going until the plan is empty.
        if (progressed && syncPasses < 12) { syncPasses++; scheduleSync(); }
        else { syncPasses = 0; }
      });
  }

  // --- DOM decoration -------------------------------------------------------

  /**
   * CUSTOM MIGRATION (v16 compatibility):
   * Return the anchor that wraps the product TITLE, never the one wrapping the
   * product image.
   *
   *   Vision 7  cart drawer : one `.cart-product-link` (title only)
   *   Vision 16 cart drawer : two `.cart-product-link` — image first, then title
   *   Vision 16 cart page   : one `.cart-product-link` (title only)
   *
   * Skipping any link inside `.product-cart-item-image` keeps this correct on
   * both surfaces and stays correct if the theme adds more image links later.
   */
  function findTitleLink(row) {
    var links = row.querySelectorAll('.cart-product-link');
    for (var i = 0; i < links.length; i++) {
      if (!links[i].closest('.product-cart-item-image')) return links[i];
    }
    return links[0] || null;
  }

  // CUSTOM FIX (17 Sep 2026): the parent bag of a gift shows its Buy X Get Y
  // rule as "(-0.00TL)". Hide discount lines whose amount is zero.
  function hideZeroDiscounts(root) {
    var lis = root.querySelectorAll('.discounts__discount');
    for (var i = 0; i < lis.length; i++) {
      if (/\(-\D*0+(?:[.,]0+)?\D*\)\s*$/.test(lis[i].textContent)) lis[i].style.display = 'none';
    }
  }

  function decorateGiftLines(root) {
    if (!root) return;
    hideZeroDiscounts(root);
    var markers = root.querySelectorAll('.is-gift-marker');
    for (var i = 0; i < markers.length; i++) {
      var row = markers[i].closest('[id^="CartItem-"], [id^="CartDrawerItem-"]');
      if (!row || row.classList.contains('is-gift-line')) continue;
      row.classList.add('is-gift-line');
      var link = findTitleLink(row);
      if (link && !row.querySelector('.gift-label')) {
        var label = document.createElement('span');
        label.className = 'gift-label';
        label.textContent = GIFT_LABEL;
        link.parentNode.insertBefore(label, link.nextSibling);
      }
    }
  }

  function decorateAllGiftLines() {
    decorateGiftLines(document.getElementById('Cart'));
    decorateGiftLines(document.getElementById('Cart-Drawer'));
  }

  function watchForRerenders() {
    ['Cart', 'Cart-Drawer'].forEach(function (id) {
      var node = document.getElementById(id);
      if (!node) return;
      new MutationObserver(function () { decorateGiftLines(node); })
        .observe(node, { childList: true, subtree: true });
    });
    decorateAllGiftLines();
  }

  // --- Boot -----------------------------------------------------------------

  function boot() {
    document.addEventListener('cart:item-added', handleItemAdded);
    document.addEventListener('line-item:change:end', scheduleSync);
    document.addEventListener('cart:refresh', scheduleSync);
    watchForRerenders();
    // Initial pass in case the page loaded with a stale gift line — an orphan,
    // or a quantity left wrong by a change made before this script ran.
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
